import { useEffect, useRef } from "react";
import { useU2000WebSocketContext } from "../../../../u2000Core/context/websocket/U2000WebSocketContext";
import {
  isNotificationMessage,
  U3141_FETCH_CADASTRAL_HISTORY_JOB_TYPE,
  U3141_FETCH_HISTORIQUE_CADASTRAL_JOB_TYPE,
} from "../../../../u2000Core/types/U2000WebSocketMessages";

/** jobTypes qui déclenchent le refetch du lot maître (historique cadastral) */
const U3141_CADASTRAL_HISTORY_JOB_TYPES = [
  U3141_FETCH_CADASTRAL_HISTORY_JOB_TYPE,
  U3141_FETCH_HISTORIQUE_CADASTRAL_JOB_TYPE, // rétrocompatibilité
];

/**
 * Écoute les notifications WebSocket "notification:new" pour le job U3141 historique cadastral.
 * Lorsque metadata.jobType === U3141_FETCH_CADASTRAL_HISTORY (ou U3141_FETCH_HISTORIQUE_CADASTRAL)
 * et metadata.masterLotChainId correspond au lot courant, appelle onHistoriqueCadastralCompleted pour rafraîchir le lot.
 */
export function useU3141HistoriqueCadastralNotification(
  masterLotChainId: string | undefined,
  onHistoriqueCadastralCompleted: (masterLotChainId: string) => void
) {
  const { on, off } = useU2000WebSocketContext();
  const onCompletedRef = useRef(onHistoriqueCadastralCompleted);
  onCompletedRef.current = onHistoriqueCadastralCompleted;

  useEffect(() => {
    if (!masterLotChainId) return;

    const handleNotification = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      const masterLotChainIdFromMsg = metadata?.masterLotChainId as string | undefined;

      if (
        typeof jobType === "string" &&
        U3141_CADASTRAL_HISTORY_JOB_TYPES.includes(jobType) &&
        masterLotChainIdFromMsg === masterLotChainId
      ) {
        if (message.importance === "error" || message.importance === "critical") {
          return;
        }
        if (import.meta.env.DEV) {
          console.log("[U3141 Historique cadastral] Notification reçue, refetch masterLotChainId:", masterLotChainId);
        }
        onCompletedRef.current(masterLotChainId);
      }
    };

    on("notification:new", handleNotification);
    return () => off("notification:new", handleNotification);
  }, [masterLotChainId, on, off]);
}
